import { useState, useEffect, useMemo } from "react";
import { Clock, Download, Search, Calendar, User, MapPin, ScanFace, CheckCircle2, AlertTriangle, Filter, ChevronDown, ChevronUp, BarChart3, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { exportTimesheetPdf } from "@/lib/pdfExport";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface TimesheetSectionProps {
  officers: Array<{ id: string; name: string; site_id: string | null }>;
  sites: Array<{ id: string; name: string }>;
}

interface ShiftRecord {
  id: string;
  officer_id: string;
  site_id: string | null;
  clock_in: string;
  clock_out: string | null;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_out_lat: number | null;
  clock_out_lng: number | null;
  clock_in_face_confidence: number;
  clock_out_face_confidence: number | null;
  total_hours: number | null;
  status: string;
  notes: string | null;
}

const CHART_COLORS = [
  "hsl(var(--app-gold))",
  "hsl(var(--app-green))",
  "hsl(var(--app-blue))",
  "hsl(var(--app-red))",
  "#a78bfa",
  "#f472b6",
  "#34d399",
  "#fbbf24",
];

const TimesheetSection = ({ officers, sites }: TimesheetSectionProps) => {
  const [records, setRecords] = useState<ShiftRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOfficer, setFilterOfficer] = useState("all");
  const [filterSite, setFilterSite] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [filterDateTo, setFilterDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [expandedOfficer, setExpandedOfficer] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "charts">("charts");
  const [chartPeriod, setChartPeriod] = useState<"weekly" | "monthly">("weekly");

  // Fetch records
  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      let query = supabase
        .from("shift_clock")
        .select("*")
        .gte("clock_in", `${filterDateFrom}T00:00:00`)
        .lte("clock_in", `${filterDateTo}T23:59:59`)
        .order("clock_in", { ascending: false });

      if (filterOfficer !== "all") query = query.eq("officer_id", filterOfficer);
      if (filterSite !== "all") query = query.eq("site_id", filterSite);

      const { data } = await query;
      setRecords(data || []);
      setLoading(false);
    };
    fetchRecords();

    const channel = supabase
      .channel("timesheet-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "shift_clock" }, () => fetchRecords())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [filterOfficer, filterSite, filterDateFrom, filterDateTo]);

  const officerMap = new Map(officers.map(o => [o.id, o]));
  const siteMap = new Map(sites.map(s => [s.id, s]));

  // Group records by officer
  const groupedByOfficer = records.reduce<Record<string, ShiftRecord[]>>((acc, r) => {
    const key = r.officer_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  // Summary per officer
  const officerSummaries = Object.entries(groupedByOfficer).map(([officerId, shifts]) => {
    const officer = officerMap.get(officerId);
    const totalHours = shifts.reduce((sum, s) => sum + (s.total_hours || 0), 0);
    const completedShifts = shifts.filter(s => s.status === "completed").length;
    const activeShift = shifts.find(s => s.status === "clocked_in");
    const avgConfidence = shifts.length > 0
      ? Math.round(shifts.reduce((sum, s) => sum + s.clock_in_face_confidence, 0) / shifts.length)
      : 0;

    return {
      officerId,
      officerName: officer?.name || "Unknown",
      siteId: officer?.site_id,
      totalHours,
      completedShifts,
      totalShifts: shifts.length,
      activeShift,
      avgConfidence,
      shifts,
    };
  }).sort((a, b) => b.totalHours - a.totalHours);

  const grandTotalHours = officerSummaries.reduce((sum, o) => sum + o.totalHours, 0);

  // ── CHART DATA ──
  const hoursPerOfficerData = useMemo(() =>
    officerSummaries.map(o => ({
      name: o.officerName.length > 10 ? o.officerName.slice(0, 10) + "…" : o.officerName,
      hours: parseFloat(o.totalHours.toFixed(1)),
      shifts: o.totalShifts,
    })),
  [officerSummaries]);

  const dailyTrendData = useMemo(() => {
    const dayMap: Record<string, { hours: number; shifts: number }> = {};
    const start = new Date(filterDateFrom);
    const end = new Date(filterDateTo);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10);
      dayMap[key] = { hours: 0, shifts: 0 };
    }
    records.forEach(r => {
      const key = r.clock_in.slice(0, 10);
      if (dayMap[key]) {
        dayMap[key].hours += r.total_hours || 0;
        dayMap[key].shifts += 1;
      }
    });
    return Object.entries(dayMap).map(([date, val]) => ({
      date: new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      hours: parseFloat(val.hours.toFixed(1)),
      shifts: val.shifts,
    }));
  }, [records, filterDateFrom, filterDateTo]);

  const weeklyMonthlyData = useMemo(() => {
    const bucketMap: Record<string, { label: string; hours: number; shifts: number }> = {};
    records.forEach(r => {
      const d = new Date(r.clock_in);
      let key: string, label: string;
      if (chartPeriod === "weekly") {
        const dayOfWeek = d.getDay();
        const startOfWeek = new Date(d);
        startOfWeek.setDate(d.getDate() - dayOfWeek);
        key = startOfWeek.toISOString().slice(0, 10);
        label = `Wk ${startOfWeek.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        label = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
      }
      if (!bucketMap[key]) bucketMap[key] = { label, hours: 0, shifts: 0 };
      bucketMap[key].hours += r.total_hours || 0;
      bucketMap[key].shifts += 1;
    });
    return Object.entries(bucketMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => ({ ...v, hours: parseFloat(v.hours.toFixed(1)) }));
  }, [records, chartPeriod]);

  // Per-officer breakdown for weekly/monthly
  const perOfficerPeriodData = useMemo(() => {
    const officerIds = [...new Set(records.map(r => r.officer_id))];
    const bucketMap: Record<string, Record<string, number> & { label: string }> = {};
    records.forEach(r => {
      const d = new Date(r.clock_in);
      let key: string, label: string;
      if (chartPeriod === "weekly") {
        const dayOfWeek = d.getDay();
        const startOfWeek = new Date(d);
        startOfWeek.setDate(d.getDate() - dayOfWeek);
        key = startOfWeek.toISOString().slice(0, 10);
        label = `Wk ${startOfWeek.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        label = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
      }
      if (!bucketMap[key]) {
        bucketMap[key] = { label } as any;
        officerIds.forEach(id => (bucketMap[key] as any)[id] = 0);
      }
      (bucketMap[key] as any)[r.officer_id] = ((bucketMap[key] as any)[r.officer_id] || 0) + (r.total_hours || 0);
    });
    return {
      data: Object.entries(bucketMap).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v),
      officerIds,
    };
  }, [records, chartPeriod]);

  const handleExportOfficerPdf = (officerId: string) => {
    const officer = officerMap.get(officerId);
    const shifts = groupedByOfficer[officerId] || [];
    if (shifts.length === 0) { toast.error("No records to export"); return; }

    exportTimesheetPdf({
      officerName: officer?.name || "Unknown",
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      shifts: shifts.map(s => ({
        date: new Date(s.clock_in).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
        clockIn: new Date(s.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        clockOut: s.clock_out ? new Date(s.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
        totalHours: s.total_hours?.toFixed(2) || "—",
        site: siteMap.get(s.site_id || "")?.name || "—",
        faceConfidence: `${s.clock_in_face_confidence}%`,
        status: s.status === "completed" ? "Completed" : s.status === "clocked_in" ? "Active" : s.status,
      })),
      totalHours: shifts.reduce((sum, s) => sum + (s.total_hours || 0), 0),
    });

    toast.success(`Timesheet exported for ${officer?.name}`);
  };

  const handleExportAllPdf = () => {
    if (records.length === 0) { toast.error("No records to export"); return; }
    officerSummaries.forEach(o => handleExportOfficerPdf(o.officerId));
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-1))] px-3 py-2 text-xs shadow-xl">
        <p className="font-medium text-[hsl(var(--app-text-primary))] mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} className="text-[hsl(var(--app-text-secondary))]">
            <span style={{ color: p.color }} className="font-bold">{p.value}h</span>
            {p.name !== "hours" && ` — ${officerMap.get(p.dataKey)?.name || p.dataKey}`}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="app-card rounded-2xl overflow-hidden">
        <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center">
                <Clock className="w-5 h-5 text-[hsl(var(--app-gold))]" />
              </div>
              <div>
                <h2 className="font-bebas text-xl text-[hsl(var(--app-text-primary))]">TIMESHEETS</h2>
                <p className="text-[10px] text-[hsl(var(--app-text-muted))]">Officer shift records with face verification</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex rounded-lg border border-[hsl(var(--app-border-subtle))] overflow-hidden">
                <button
                  onClick={() => setViewMode("charts")}
                  className={`px-3 py-1.5 text-[10px] font-medium transition-colors flex items-center gap-1 ${viewMode === "charts" ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"}`}
                >
                  <BarChart3 className="w-3 h-3" /> Charts
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`px-3 py-1.5 text-[10px] font-medium transition-colors flex items-center gap-1 ${viewMode === "table" ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))]"}`}
                >
                  <Calendar className="w-3 h-3" /> Table
                </button>
              </div>
              <button
                onClick={handleExportAllPdf}
                disabled={records.length === 0}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] text-xs font-medium hover:bg-[hsl(var(--app-gold)/0.25)] transition-colors disabled:opacity-40"
              >
                <Download className="w-3.5 h-3.5" />
                Export All
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Officer</label>
              <select
                value={filterOfficer}
                onChange={e => setFilterOfficer(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] outline-none"
              >
                <option value="all">All Officers</option>
                {officers.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">Site</label>
              <select
                value={filterSite}
                onChange={e => setFilterSite(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] outline-none"
              >
                <option value="all">All Sites</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">From</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mb-1 block">To</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-xs text-[hsl(var(--app-text-primary))] outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="app-card rounded-xl p-4 text-center">
          <p className="font-mono-data text-2xl font-bold text-[hsl(var(--app-gold))]">{grandTotalHours.toFixed(1)}</p>
          <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mt-1">Total Hours</p>
        </div>
        <div className="app-card rounded-xl p-4 text-center">
          <p className="font-mono-data text-2xl font-bold text-[hsl(var(--app-green))]">{records.filter(r => r.status === "completed").length}</p>
          <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mt-1">Completed Shifts</p>
        </div>
        <div className="app-card rounded-xl p-4 text-center">
          <p className="font-mono-data text-2xl font-bold text-[hsl(var(--app-blue))]">{records.filter(r => r.status === "clocked_in").length}</p>
          <p className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider mt-1">Active Now</p>
        </div>
      </div>

      {/* ── CHARTS VIEW ── */}
      {viewMode === "charts" && !loading && records.length > 0 && (
        <div className="space-y-4">
          {/* Period toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChartPeriod("weekly")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${chartPeriod === "weekly" ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]" : "text-[hsl(var(--app-text-muted))] border border-[hsl(var(--app-border-subtle))] hover:text-[hsl(var(--app-text-primary))]"}`}
            >
              Weekly
            </button>
            <button
              onClick={() => setChartPeriod("monthly")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${chartPeriod === "monthly" ? "bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.3)]" : "text-[hsl(var(--app-text-muted))] border border-[hsl(var(--app-border-subtle))] hover:text-[hsl(var(--app-text-primary))]"}`}
            >
              Monthly
            </button>
          </div>

          {/* Hours per Officer Bar Chart */}
          <div className="app-card rounded-2xl overflow-hidden">
            <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-[hsl(var(--app-gold))]" />
                <h3 className="font-bebas text-base text-[hsl(var(--app-text-primary))]">HOURS PER OFFICER</h3>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursPerOfficerData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--app-border-subtle))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--app-text-muted))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--app-text-muted))" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="hours" radius={[6, 6, 0, 0]}>
                      {hoursPerOfficerData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Daily Hours Trend */}
          <div className="app-card rounded-2xl overflow-hidden">
            <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-green)), transparent)" }} />
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-[hsl(var(--app-green))]" />
                <h3 className="font-bebas text-base text-[hsl(var(--app-text-primary))]">DAILY HOURS TREND</h3>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrendData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--app-border-subtle))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: "hsl(var(--app-text-muted))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--app-text-muted))" }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="hours" stroke="hsl(var(--app-green))" strokeWidth={2} dot={{ r: 3, fill: "hsl(var(--app-green))" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Weekly/Monthly Stacked Chart per Officer */}
          {perOfficerPeriodData.data.length > 0 && (
            <div className="app-card rounded-2xl overflow-hidden">
              <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-blue)), transparent)" }} />
              <div className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-[hsl(var(--app-blue))]" />
                  <h3 className="font-bebas text-base text-[hsl(var(--app-text-primary))]">
                    {chartPeriod === "weekly" ? "WEEKLY" : "MONTHLY"} BREAKDOWN BY OFFICER
                  </h3>
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 mb-3">
                  {perOfficerPeriodData.officerIds.map((id, i) => (
                    <div key={id} className="flex items-center gap-1.5 text-[10px] text-[hsl(var(--app-text-secondary))]">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {officerMap.get(id)?.name || "Unknown"}
                    </div>
                  ))}
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={perOfficerPeriodData.data} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--app-border-subtle))" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: "hsl(var(--app-text-muted))" }} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--app-text-muted))" }} />
                      <Tooltip content={<CustomTooltip />} />
                      {perOfficerPeriodData.officerIds.map((id, i) => (
                        <Bar key={id} dataKey={id} stackId="officers" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === perOfficerPeriodData.officerIds.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TABLE VIEW ── */}
      {(viewMode === "table" || (viewMode === "charts" && records.length === 0)) && !loading && (
        <>
          {officerSummaries.length === 0 ? (
            <div className="app-card rounded-2xl p-8 text-center">
              <Clock className="w-8 h-8 text-[hsl(var(--app-text-muted))] mx-auto mb-3" />
              <p className="text-sm text-[hsl(var(--app-text-secondary))]">No shift records found for the selected period</p>
            </div>
          ) : (
            <div className="space-y-3">
              {officerSummaries.map(summary => {
                const isExpanded = expandedOfficer === summary.officerId;
                return (
                  <div key={summary.officerId} className="app-card rounded-2xl overflow-hidden">
                    <button
                      onClick={() => setExpandedOfficer(isExpanded ? null : summary.officerId)}
                      className="w-full p-4 flex items-center justify-between hover:bg-[hsl(var(--app-surface-2)/0.5)] transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[hsl(var(--app-gold)/0.1)] border border-[hsl(var(--app-gold)/0.2)] flex items-center justify-center">
                          <User className="w-5 h-5 text-[hsl(var(--app-gold))]" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-bold text-[hsl(var(--app-text-primary))]">{summary.officerName}</p>
                          <p className="text-[10px] text-[hsl(var(--app-text-muted))]">
                            {summary.completedShifts} shifts · {summary.avgConfidence}% avg face match
                            {summary.activeShift && (
                              <span className="ml-2 px-1.5 py-0.5 rounded bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))] text-[9px] font-semibold">ON SHIFT</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-mono-data text-lg font-bold text-[hsl(var(--app-gold))]">{summary.totalHours.toFixed(1)}h</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExportOfficerPdf(summary.officerId); }}
                          className="p-2 rounded-lg hover:bg-[hsl(var(--app-gold)/0.15)] text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-gold))] transition-colors"
                          title="Download PDF Timesheet"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-[hsl(var(--app-text-muted))]" /> : <ChevronDown className="w-4 h-4 text-[hsl(var(--app-text-muted))]" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[hsl(var(--app-border-subtle))] px-4 pb-4">
                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[10px] text-[hsl(var(--app-text-muted))] uppercase tracking-wider">
                                <th className="text-left py-2 px-2">Date</th>
                                <th className="text-left py-2 px-2">Clock In</th>
                                <th className="text-left py-2 px-2">Clock Out</th>
                                <th className="text-left py-2 px-2">Hours</th>
                                <th className="text-left py-2 px-2">Site</th>
                                <th className="text-left py-2 px-2">Face %</th>
                                <th className="text-left py-2 px-2">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.shifts.map(shift => (
                                <tr key={shift.id} className="border-t border-[hsl(var(--app-border-subtle)/0.5)]">
                                  <td className="py-2 px-2 text-[hsl(var(--app-text-primary))]">
                                    {new Date(shift.clock_in).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                  </td>
                                  <td className="py-2 px-2 text-[hsl(var(--app-text-primary))]">
                                    {new Date(shift.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                  </td>
                                  <td className="py-2 px-2 text-[hsl(var(--app-text-primary))]">
                                    {shift.clock_out ? new Date(shift.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                                  </td>
                                  <td className="py-2 px-2 font-bold text-[hsl(var(--app-gold))]">
                                    {shift.total_hours?.toFixed(1) || "—"}h
                                  </td>
                                  <td className="py-2 px-2 text-[hsl(var(--app-text-secondary))]">
                                    {siteMap.get(shift.site_id || "")?.name || "—"}
                                  </td>
                                  <td className="py-2 px-2">
                                    <span className={`${shift.clock_in_face_confidence >= 80 ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-gold))]"}`}>
                                      {shift.clock_in_face_confidence}%
                                    </span>
                                  </td>
                                  <td className="py-2 px-2">
                                    {shift.status === "completed" ? (
                                      <span className="flex items-center gap-1 text-[hsl(var(--app-green))]">
                                        <CheckCircle2 className="w-3 h-3" /> Done
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-[hsl(var(--app-blue))]">
                                        <div className="w-2 h-2 rounded-full bg-[hsl(var(--app-green))] animate-pulse" /> Active
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-[hsl(var(--app-gold))] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default TimesheetSection;
